"use client";

import { c as _c } from "react/compiler-runtime";
import { Heart, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DiscordCommunityLink } from "@/components/fan-clubs/discord-community-link";
import { FanClubAnnouncementList } from "@/components/fan-clubs/fan-club-announcement-list";
import type { FanClubPublicSummary } from "@/lib/fan-clubs/types";
import { useFanClubMembership } from "@/hooks/use-fan-club-membership";
function memberLabel(count: number): string {
  return `${count.toLocaleString()} ${count === 1 ? "member" : "members"}`;
}
export function StoryFanClubSection(t0) {
  const $ = _c(50);
  const {
    storyId,
    storySlug,
    initialFanClub
  } = t0;
  let t1;
  if ($[0] !== initialFanClub || $[1] !== storyId || $[2] !== storySlug) {
    t1 = {
      storyId,
      storySlug,
      initialFanClub
    };
    $[0] = initialFanClub;
    $[1] = storyId;
    $[2] = storySlug;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    fanClub,
    memberFanClub,
    isMember,
    isLoadingViewer,
    isPending,
    error,
    toggleMembership,
    loadViewerState
  } = useFanClubMembership(t1);
  if (!fanClub || fanClub.status !== "active") {
    return null;
  }
  const membershipLabel = isMember ? "Leave fan club" : "Join fan club";
  let t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="rounded-full bg-indigo-500/15 p-2 text-indigo-500 dark:text-indigo-300"><Heart className="size-5" aria-hidden="true" /></span>;
    t3 = <Badge variant="secondary">Story fan club</Badge>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== fanClub.memberCount) {
    t4 = memberLabel(fanClub.memberCount);
    $[6] = fanClub.memberCount;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <div className="flex flex-wrap items-center gap-2">{t2}{t3}<span className="text-sm text-muted-foreground">{t4}</span></div>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== fanClub.name) {
    t6 = <h2 id="fan-club-heading" className="mt-4 font-heading text-2xl font-bold text-foreground">{fanClub.name}</h2>;
    $[10] = fanClub.name;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = fanClub.description || "Join readers following this story and get updates directly from the author.";
  let t8;
  if ($[12] !== t7) {
    t8 = <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t7}</p>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== isMember || $[15] !== memberFanClub) {
    t9 = isMember && memberFanClub?.discord && <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground"><MessageCircle className="size-3.5" aria-hidden="true" />The invite opens{" "}{memberFanClub.discord.guildName ?? "the connected Discord server"}{memberFanClub.discord.inviteChannelName ? ` #${memberFanClub.discord.inviteChannelName}` : ""}. Iloris never reads Discord chat.</p>;
    $[14] = isMember;
    $[15] = memberFanClub;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t5 || $[18] !== t6 || $[19] !== t8 || $[20] !== t9) {
    t10 = <div className="max-w-2xl">{t5}{t6}{t8}{t9}</div>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = isMember ? "outline" : "default";
  const t12 = isLoadingViewer || isPending || Boolean(error);
  const t13 = isMember ? "size-4 fill-current" : "size-4";
  let t14;
  if ($[22] !== t13) {
    t14 = <Heart className={t13} aria-hidden="true" />;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const t15 = isLoadingViewer ? "Checking membership\u2026" : isPending ? "Saving\u2026" : membershipLabel;
  let t16;
  if ($[24] !== t11 || $[25] !== t12 || $[26] !== t14 || $[27] !== t15 || $[28] !== toggleMembership) {
    t16 = <Button variant={t11} disabled={t12} onClick={toggleMembership}>{t14}{t15}</Button>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t14;
    $[27] = t15;
    $[28] = toggleMembership;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== isMember || $[31] !== memberFanClub) {
    t17 = isMember && memberFanClub?.discordInviteUrl && <DiscordCommunityLink inviteUrl={memberFanClub.discordInviteUrl} />;
    $[30] = isMember;
    $[31] = memberFanClub;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t16 || $[34] !== t17) {
    t18 = <div className="flex shrink-0 flex-wrap gap-2">{t16}{t17}</div>;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t10 || $[37] !== t18) {
    t19 = <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">{t10}{t18}</div>;
    $[36] = t10;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== error || $[40] !== loadViewerState) {
    t20 = error && <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-destructive" role="alert"><span>{error}</span><Button size="sm" variant="outline" onClick={() => void loadViewerState()}>Try again</Button></div>;
    $[39] = error;
    $[40] = loadViewerState;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">Recent updates</h3>;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== isMember || $[44] !== memberFanClub?.recentAnnouncements) {
    t22 = <div className="mt-6 border-t border-border/60 pt-5">{t21}{isMember ? <FanClubAnnouncementList announcements={memberFanClub?.recentAnnouncements ?? []} /> : <p className="text-sm text-muted-foreground">Join this fan club to see author and chapter updates.</p>}</div>;
    $[43] = isMember;
    $[44] = memberFanClub?.recentAnnouncements;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t19 || $[47] !== t20 || $[48] !== t22) {
    t23 = <section id="fan-club" aria-labelledby="fan-club-heading" className="mt-10 scroll-mt-24 rounded-2xl border border-indigo-400/20 bg-gradient-to-br from-indigo-500/10 via-card to-violet-500/5 p-5 shadow-lg sm:p-7">{t19}{t20}{t22}</section>;
    $[46] = t19;
    $[47] = t20;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  return t23;
}